import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
  Card,
  CardContent,
  CardMedia,
  CardActions,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  VisibilityOff,
  Image as ImageIcon,
  Refresh,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { Category, CATEGORY_PLATFORM_LABELS } from '../../types';
import toast from 'react-hot-toast';
import CategoryForm from './CategoryForm';
import { getImageUrlWithFallback } from '../../utils/imageUrlHelper';

// Helper function to get display name (handles both string and multilingual formats)
const getDisplayName = (name: string | Record<string, string> | undefined): string => {
  if (!name) return '';
  if (typeof name === 'string') return name;
  // Multilingual format - prefer EN, fallback to first available
  return name.EN || name.en || Object.values(name)[0] || '';
};

// Helper function to get display description
const getDisplayDescription = (description: string | Record<string, string> | undefined): string => {
  if (!description) return '';
  if (typeof description === 'string') return description;
  // Multilingual format - prefer EN, fallback to first available
  return description.EN || description.en || Object.values(description)[0] || '';
};

const CategoriesList: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<boolean | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<Category | null>(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const filters: any = {};
      if (filterActive !== null) {
        filters.isActive = filterActive;
      }
      if (searchTerm) {
        filters.search = searchTerm;
      }
      const response = await apiService.getAllCategories(filters);
      if (response.success && response.data) {
        // Sort categories by displayOrder
        const sortedCategories = [...response.data].sort((a, b) => a.displayOrder - b.displayOrder);
        setCategories(sortedCategories);
      } else {
        setError(response.error || 'Failed to fetch categories');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to fetch categories');
      toast.error('Failed to fetch categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [filterActive]);

  const handleCreate = () => {
    setSelectedCategory(null);
    setFormDialogOpen(true);
  };

  const handleEdit = (category: Category) => {
    setSelectedCategory(category);
    setFormDialogOpen(true);
  };

  const handleDelete = (category: Category) => {
    setCategoryToDelete(category);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      const response = await apiService.deleteCategory(categoryToDelete._id);
      if (response.success) {
        toast.success('Category deleted successfully');
        fetchCategories();
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
      } else {
        toast.error(response.error || 'Failed to delete category');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete category');
    }
  };

  const handleToggleStatus = async (category: Category) => {
    try {
      const response = await apiService.toggleCategoryStatus(category._id);
      if (response.success && response.data) {
        toast.success(`Category ${response.data.isActive ? 'activated' : 'deactivated'}`);
        fetchCategories();
      } else {
        toast.error(response.error || 'Failed to toggle category status');
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to toggle category status');
    }
  };

  const handleFormClose = () => {
    setFormDialogOpen(false);
    setSelectedCategory(null);
    fetchCategories();
  };

  const handleMoveCategory = async (categoryId: string, direction: 'up' | 'down') => {
    // Work with all categories (not filtered) to maintain proper ordering
    const currentIndex = categories.findIndex(cat => cat._id === categoryId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;

    // Create new array with swapped items
    const newCategories = [...categories];
    const tempCategory = newCategories[currentIndex];
    newCategories[currentIndex] = newCategories[newIndex];
    newCategories[newIndex] = tempCategory;

    // Update displayOrder values for all categories
    const reorderedCategories = newCategories.map((cat, index) => ({
      ...cat,
      displayOrder: index,
    }));

    setCategories(reorderedCategories);

    // Update both swapped categories on server
    try {
      await apiService.updateCategory(newCategories[currentIndex]._id, {
        displayOrder: reorderedCategories[currentIndex].displayOrder,
      });
      await apiService.updateCategory(newCategories[newIndex]._id, {
        displayOrder: reorderedCategories[newIndex].displayOrder,
      });
      toast.success('Category order updated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update category order');
      // Revert on error
      fetchCategories();
    }
  };

  const filteredCategories = categories
    .filter(category => {
      if (filterActive !== null && category.isActive !== filterActive) return false;
      if (!searchTerm) return true;
      const searchLower = searchTerm.toLowerCase();
      const displayName = getDisplayName(category.name).toLowerCase();
      const displayDesc = getDisplayDescription(category.description).toLowerCase();
      return (
        displayName.includes(searchLower) ||
        displayDesc.includes(searchLower)
      );
    })
    .sort((a, b) => a.displayOrder - b.displayOrder);

  if (loading && categories.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Categories</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreate}
        >
          Create Category
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            label="Search Categories"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ flexGrow: 1, minWidth: 200 }}
          />
          <FormControlLabel
            control={
              <Switch
                checked={filterActive === true}
                onChange={(e) => setFilterActive(e.target.checked ? true : null)}
              />
            }
            label="Active Only"
          />
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchCategories}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      <Grid container spacing={2}>
        {filteredCategories.map((category, filteredIndex) => {
          const categoryIndex = categories.findIndex(c => c._id === category._id);
          return (
          <Grid item xs={6} sm={4} md={3} lg={2} key={category._id}>
            <Card
              sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4,
                },
              }}
              onClick={() => handleEdit(category)}
            >
              {/* Category Image */}
              <CardMedia
                component="img"
                height="200"
                image={getImageUrlWithFallback(category.icon)}
                alt={getDisplayName(category.name)}
                sx={{
                  objectFit: 'cover',
                  backgroundColor: 'grey.200',
                }}
                onError={(e: any) => {
                  e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                }}
              />
              
              {/* Category Content */}
              <CardContent sx={{ flexGrow: 1, p: 1.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="start" mb={1}>
                  <Typography 
                    variant="subtitle2" 
                    sx={{ 
                      fontWeight: 600,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      flex: 1,
                      mr: 0.5
                    }}
                  >
                    {getDisplayName(category.name)}
                  </Typography>
                  <Chip
                    label={category.isActive ? 'Active' : 'Inactive'}
                    color={category.isActive ? 'success' : 'default'}
                    size="small"
                    sx={{ height: '20px', fontSize: '0.65rem' }}
                  />
                </Box>
                
                <Box display="flex" alignItems="center" gap={0.5} mb={1}>
                  <ImageIcon fontSize="small" sx={{ fontSize: '14px', color: 'text.secondary' }} />
                  <Typography variant="caption" color="text.secondary">
                    {category.templateImages?.length || 0} templates
                  </Typography>
                </Box>

                {(category.hiddenPlatforms?.length ?? 0) > 0 && (
                  <Chip
                    label={`Hidden: ${category.hiddenPlatforms!.map((p) => CATEGORY_PLATFORM_LABELS[p]).join(', ')}`}
                    color="warning"
                    variant="outlined"
                    size="small"
                    sx={{ height: '20px', fontSize: '0.65rem' }}
                  />
                )}
              </CardContent>
              
              {/* Actions */}
              <CardActions sx={{ p: 1, pt: 0, justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
                <Box display="flex" gap={0.5}>
                  <Tooltip title="Move up">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveCategory(category._id, 'up');
                      }}
                      disabled={categoryIndex === 0}
                    >
                      <ArrowUpward fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveCategory(category._id, 'down');
                      }}
                      disabled={categoryIndex === categories.length - 1}
                    >
                      <ArrowDownward fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box display="flex" gap={0.5}>
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(category);
                      }}
                    >
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={category.isActive ? 'Deactivate' : 'Activate'}>
                    <IconButton
                      size="small"
                      color={category.isActive ? 'default' : 'success'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(category);
                      }}
                    >
                      {category.isActive ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(category);
                      }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardActions>
            </Card>
          </Grid>
          );
        })}
      </Grid>

      {filteredCategories.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            No categories found
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreate}
            sx={{ mt: 2 }}
          >
            Create First Category
          </Button>
        </Paper>
      )}

      {/* Create/Edit Dialog */}
      <CategoryForm
        open={formDialogOpen}
        onClose={handleFormClose}
        category={selectedCategory}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Category</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{getDisplayName(categoryToDelete?.name)}"? This action cannot be undone.
          </Typography>
          {categoryToDelete?.templateImages?.length && categoryToDelete.templateImages.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This category has {categoryToDelete.templateImages.length} template images that will also be removed.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CategoriesList;

